
import links from "@/config/links";
import messages from "@/config/messages";
import { defineComponent } from "vue";
import helper from "@/utils/helper";
import common from "@/config/common";
import * as apiClient from "@/apiclient/";
import { mapGetters, mapMutations } from "vuex";
import routesPath from "@/config/routesPath";

export default defineComponent({
  data() {
    return {
      links,
      messages,
      validationMsg: "",
      paymentType: 1,
      profileData: {} as apiClient.ProfileResponseDto,
      qualifyingBeneficiaryData: {} as apiClient.QualifyingBeneficiaryDto,
      dependentData: [] as apiClient.GetAllDependentDto[],
      documentsData: [] as apiClient.FileResponse[],
      amountTodayData: {} as apiClient.GetAmountDueTodayDto,
      monthlyPremiumData: {} as apiClient.GetMonthlyPremiumDto,
      benefitsData: [] as apiClient.ParticipantBenefitsDto[],
      getMonthlyStateName: "",
      getCountryName: "",
      getMonthlyCountryName: "",
      getStateName: "",
      totalAmountDue: 0,
      genderOptions: [] as apiClient.GenderDto[],
      countries: {} as any,
      accountTypes: {} as any,
      cardDetail: {} as any,
      isStripeEnabled: false,
      confirmationData: null,
      specialOpenEnrollmentCreated: false,
    };
  },
  components: {},
  computed: {
    ...mapGetters([
      "getEnrollment",
      "getTotalEnrollments",
      "getWasSpecialOpenEnrollmentCreated",
    ]),

    appSubmitted() {
      return this.getEnrollment?.cobra?.status === 10;
    },
    paymentMethod: () => common.paymentMethods,
  },
  async created() {
    await this.getGender();
    await this.getStripeEnabled();
    await this.getAccountTypes();
    await this.getAllData();
    await this.getLocations();
    this.specialOpenEnrollmentCreated = this.getWasSpecialOpenEnrollmentCreated;
  },
  methods: {
    ...mapMutations(["setEnrollment"]),
    async getAllData(): Promise<void> {
      const enrollment = this.getEnrollment;

      const result = await apiClient
        .confirmationController()
        .getApiConfirmation({
          customerParticipantId: enrollment.customerParticipantId,
        });

      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        if (result.data.getPaymentAmountDueToday) {
          this.paymentType = result.data.getPaymentAmountDueToday.payMethod!;
        }
        this.profileData = result.data.profile!;
        this.qualifyingBeneficiaryData = result.data.qualifyingBeneficiary!;
        this.dependentData = result.data.dependents!;
        this.documentsData = result.data.documents!;
        this.amountTodayData = result.data.getPaymentAmountDueToday!;
        this.monthlyPremiumData = result.data.getPaymentMonthlyPremium!;
        this.benefitsData = result.data.benefits!;
        this.cardDetail = result.data.cardDetail!;
      }
      await this.syncEnrollment();
    },
    async syncEnrollment(): Promise<void> {
      const result = await apiClient
        .userController()
        .getApiUserService();

      if (!result || result.error || !result.data?.length) {
        return;
      }

      const enrollment = result.data.find(
        (e: any) =>
          e.serviceType === common.serviceType.Cobra &&
          e.cobra?.status === 10
      );

      if (!enrollment) {
        return;
      }

      this.setEnrollment(enrollment);
      sessionStorage.setItem(
        common.storageVariables.enrollment,
        JSON.stringify(enrollment)
      );
    },
    async getAccountTypes(): Promise<void> {
      const result = await apiClient
        .paymentController()
        .getApiPaymentAccountType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.value) {
        this.accountTypes = result.data.value;
      }
    },
    async getLocations() {
      let locations = await helper.getLocationDetails(
        this.profileData.countryId!,
        this.profileData.geoStateId!,
        this.monthlyPremiumData.billingCountryId!,
        this.monthlyPremiumData.billingGeoStateId!
      );
      this.getCountryName = locations.getCountryName;
      this.getMonthlyCountryName = locations.getMonthlyCountryName;
      this.getStateName = locations.getStateName;
      this.getMonthlyStateName = locations.getMonthlyStateName;
      this.countries = locations.countries;
    },
    async downloadAsPDF() {
      await this.getConfirmationPDF();
    },
    async getConfirmationPDF(): Promise<void> {
      const result = await apiClient
        .confirmationController()
        .getApiConfirmationGetFile({
          customerParticipantId: this.getEnrollment.customerParticipantId,
        });

      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      }
      if (!result.data) {
        return;
      }
      const blob = new Blob([helper.base64ToUint8Array(result.data)], {
        type: "application/pdf",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = "summary.pdf";
      link.click();

      // Clean up the URL and link element
      window.URL.revokeObjectURL(url);
    },
    getEnrollmentDate() {
      return (
        helper.formatDate(
          this.profileData.enrollmentDate!,
          "MM/DD/YYYY hh:mm A"
        ) +
        " " +
        common.timezone.ET
      );
    },
    async getGender(): Promise<void> {
      let genderData = await helper.getGenders();
      if (genderData.validationMsg.length > 0) {
        this.validationMsg = genderData.validationMsg;
      } else {
        this.genderOptions = genderData.genderOptions;
      }
    },
    async getStripeEnabled() {
      let stripeData = await helper.getStripeEnabled();
      if (stripeData) {
        if (stripeData.validationMsg.length > 0) {
          this.validationMsg = stripeData.validationMsg;
        } else {
          this.isStripeEnabled = stripeData.isStripeEnabled;
        }
      }
    },
    async startSpecialEnrollment() {
      if (!this.specialOpenEnrollmentCreated) return;

      this.$router.push({
        name: routesPath.enrollments,
      });
    },
  },
});
